/**
 * Customer-facing Support page (/support)
 * ----------------------------------------
 * Two-pane layout:
 *  • Left: "New ticket" form (subject, description, category, priority)
 *  • Right: "My tickets" list. Click a row → inline thread with reply box.
 *
 * Reads ?ticket=TKT-XXXX from the URL on mount to deep-link from email CTAs.
 */
import { useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  LifeBuoy, Send, Loader2, AlertCircle, MessageSquare, Clock,
  CheckCircle2, ChevronRight, RefreshCw,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useWorkspace } from '../context/WorkspaceContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'technical', label: 'Technical issue' },
  { value: 'billing', label: 'Billing & payments' },
  { value: 'account', label: 'Account & access' },
  { value: 'bug_report', label: 'Bug report' },
  { value: 'feature_request', label: 'Feature request' },
  { value: 'other', label: 'Other' },
];

const PRIORITIES = [
  { value: 'low', label: 'Low — no rush' },
  { value: 'medium', label: 'Medium — normal' },
  { value: 'high', label: 'High — blocking work' },
];

const STATUS_META = {
  open:              { label: 'Open',           color: 'bg-blue-500/15 text-blue-700 dark:text-blue-300',     icon: AlertCircle },
  in_progress:       { label: 'In progress',    color: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',  icon: Clock },
  waiting_customer:  { label: 'Waiting on you', color: 'bg-purple-500/15 text-purple-700 dark:text-purple-300', icon: MessageSquare },
  resolved:          { label: 'Resolved',       color: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300', icon: CheckCircle2 },
  closed:            { label: 'Closed',         color: 'bg-zinc-500/15 text-zinc-700 dark:text-zinc-300',     icon: CheckCircle2 },
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.open;
  const Icon = meta.icon;
  return (
    <Badge className={`text-[10px] ${meta.color}`}>
      <Icon className="w-3 h-3 mr-1" />
      {meta.label}
    </Badge>
  );
}

export default function SupportPage() {
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkedTicket = searchParams.get('ticket');

  const [tickets, setTickets] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [selectedId, setSelectedId] = useState(deepLinkedTicket || null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // New ticket form
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('technical');
  const [priority, setPriority] = useState('medium');
  const [submitBusy, setSubmitBusy] = useState(false);

  // Reply form
  const [replyContent, setReplyContent] = useState('');
  const [replyBusy, setReplyBusy] = useState(false);

  const fetchList = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/support/tickets`);
      if (r.ok) {
        const j = await r.json();
        setTickets(j.tickets || []);
      }
    } catch (e) {
      // silent
    } finally {
      setLoadingList(false);
    }
  }, []);

  const fetchDetail = useCallback(async (ticketId) => {
    if (!ticketId) { setDetail(null); return; }
    setLoadingDetail(true);
    try {
      const r = await fetch(`${API_URL}/api/support/tickets/${ticketId}`);
      if (r.ok) setDetail(await r.json());
      else if (r.status === 404) { setDetail(null); toast.error('Ticket not found'); }
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  // One-shot mount fetch (guarded by a flag — same pattern as TeamSettings)
  const [loadedOnce, setLoadedOnce] = useState(false);
  if (!loadedOnce) {
    setLoadedOnce(true);
    queueMicrotask(() => {
      fetchList();
      if (deepLinkedTicket) fetchDetail(deepLinkedTicket);
    });
  }

  const handleSelect = (id) => {
    setSelectedId(id);
    setSearchParams({ ticket: id });
    fetchDetail(id);
  };

  const handleSubmit = async () => {
    if (!subject.trim() || subject.trim().length < 3) {
      toast.error('Please give your ticket a short subject (3+ characters).');
      return;
    }
    if (!description.trim() || description.trim().length < 10) {
      toast.error('Please describe the issue in at least 10 characters.');
      return;
    }
    setSubmitBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/support/tickets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject: subject.trim(),
          description: description.trim(),
          category,
          priority,
          page_url: window.location.href,
          workspace_id: currentWorkspace?.workspace_id || null,
        }),
      });
      const data = await r.json();
      if (!r.ok) {
        toast.error(data.detail || 'Failed to submit ticket');
        return;
      }
      toast.success(`Ticket ${data.ticket_id} created — we'll be in touch shortly.`);
      setSubject(''); setDescription(''); setCategory('technical'); setPriority('medium');
      await fetchList();
      handleSelect(data.ticket_id);
    } finally {
      setSubmitBusy(false);
    }
  };

  const handleReply = async () => {
    if (!replyContent.trim() || !selectedId) return;
    setReplyBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/support/tickets/${selectedId}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: replyContent.trim() }),
      });
      const data = await r.json();
      if (!r.ok) {
        toast.error(data.detail || 'Failed to send reply');
        return;
      }
      setReplyContent('');
      toast.success('Reply sent');
      await Promise.all([fetchDetail(selectedId), fetchList()]);
    } finally {
      setReplyBusy(false);
    }
  };

  const fmtTime = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  const sortedTickets = useMemo(
    () => [...tickets].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || '')),
    [tickets],
  );

  return (
    <div className="container max-w-7xl mx-auto py-8 px-4" data-testid="support-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <LifeBuoy className="w-7 h-7 text-emerald-500" />
            Help &amp; Support
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Have a question or run into a snag? Send our team a ticket and we&apos;ll get back to you fast.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => { fetchList(); if (selectedId) fetchDetail(selectedId); }}
          data-testid="support-refresh-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* ── New ticket form ──────────────────────────────────────── */}
        <Card className="lg:col-span-2 h-fit" data-testid="new-ticket-card">
          <CardHeader>
            <CardTitle className="text-base">Submit a new ticket</CardTitle>
            <CardDescription>
              Logged in as <strong>{user?.email}</strong>
              {currentWorkspace?.name ? <> · workspace: <strong>{currentWorkspace.name}</strong></> : null}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <label className="text-xs font-medium mb-1 block uppercase tracking-wide text-muted-foreground">Subject</label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Short summary (e.g. 'Stripe checkout returns 400')"
                maxLength={200}
                data-testid="ticket-subject"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium mb-1 block uppercase tracking-wide text-muted-foreground">Category</label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger data-testid="ticket-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block uppercase tracking-wide text-muted-foreground">Priority</label>
                <Select value={priority} onValueChange={setPriority}>
                  <SelectTrigger data-testid="ticket-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block uppercase tracking-wide text-muted-foreground">Describe what&apos;s happening</label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Steps to reproduce, what you expected, screenshots etc."
                rows={7}
                maxLength={10000}
                data-testid="ticket-description"
              />
            </div>
            <Button
              onClick={handleSubmit}
              disabled={submitBusy || !subject || !description}
              className="bg-emerald-500 hover:bg-emerald-600 text-white w-full"
              data-testid="ticket-submit"
            >
              {submitBusy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <Send className="w-4 h-4 mr-2" />
              Send to support
            </Button>
          </CardContent>
        </Card>

        {/* ── My tickets list + detail ─────────────────────────────── */}
        <Card className="lg:col-span-3" data-testid="my-tickets-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center justify-between">
              <span>My tickets</span>
              <span className="text-xs text-muted-foreground font-normal">
                {tickets.length} total
              </span>
            </CardTitle>
            <CardDescription>
              {selectedId ? 'Viewing the selected ticket.' : 'Select a ticket below to see the thread.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingList ? (
              <div className="space-y-2">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            ) : sortedTickets.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground">
                <LifeBuoy className="w-10 h-10 mx-auto mb-2 opacity-40" />
                <p className="text-sm">You haven&apos;t opened any tickets yet.</p>
                <p className="text-xs mt-1">Use the form on the left if you need a hand.</p>
              </div>
            ) : (
              <div className="divide-y border rounded-lg max-h-80 overflow-y-auto">
                {sortedTickets.map(t => (
                  <button
                    key={t.ticket_id}
                    onClick={() => handleSelect(t.ticket_id)}
                    className={`w-full text-left px-3 py-2.5 hover:bg-muted/40 transition-colors flex items-center gap-2 ${selectedId === t.ticket_id ? 'bg-muted/60' : ''}`}
                    data-testid={`ticket-row-${t.ticket_id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-[11px] text-muted-foreground">{t.ticket_id}</span>
                        <StatusBadge status={t.status} />
                      </div>
                      <p className="text-sm font-medium truncate mt-0.5">{t.subject}</p>
                      <p className="text-xs text-muted-foreground">{fmtTime(t.created_at)}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  </button>
                ))}
              </div>
            )}

            {/* Detail view */}
            {selectedId && (
              <div className="mt-5 border-t pt-5" data-testid="ticket-thread">
                {loadingDetail ? (
                  <Skeleton className="h-40 w-full" />
                ) : detail ? (
                  <>
                    <div className="mb-4">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div>
                          <p className="font-mono text-[11px] text-muted-foreground">{detail.ticket_id}</p>
                          <h3 className="font-semibold text-lg">{detail.subject}</h3>
                        </div>
                        <StatusBadge status={detail.status} />
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Opened {fmtTime(detail.created_at)}
                        {detail.assigned_to_name && <> · Assigned to {detail.assigned_to_name}</>}
                      </p>
                    </div>

                    <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
                      {/* Original description as the first message */}
                      <div className="rounded-lg border bg-zinc-50 dark:bg-zinc-900/40 p-3">
                        <p className="text-[11px] text-muted-foreground mb-1">
                          You · {fmtTime(detail.created_at)}
                        </p>
                        <p className="text-sm whitespace-pre-wrap">{detail.description}</p>
                      </div>
                      {(detail.comments || []).map(c => (
                        <div
                          key={c.comment_id}
                          className={`rounded-lg border p-3 ${c.author_kind === 'admin' ? 'bg-emerald-50/60 dark:bg-emerald-950/20 border-emerald-200/40' : 'bg-zinc-50 dark:bg-zinc-900/40'}`}
                          data-testid={`comment-${c.comment_id}`}
                        >
                          <p className="text-[11px] text-muted-foreground mb-1">
                            {c.author_kind === 'admin' ? '🛟 ' : ''}{c.author_name} · {fmtTime(c.created_at)}
                          </p>
                          <p className="text-sm whitespace-pre-wrap">{c.content}</p>
                        </div>
                      ))}
                    </div>

                    {/* Reply box — only when not closed */}
                    {detail.status !== 'closed' && (
                      <div className="mt-4 space-y-2">
                        <Textarea
                          value={replyContent}
                          onChange={(e) => setReplyContent(e.target.value)}
                          placeholder="Type your reply…"
                          rows={3}
                          data-testid="ticket-reply-input"
                        />
                        <div className="flex justify-end">
                          <Button
                            onClick={handleReply}
                            disabled={replyBusy || !replyContent.trim()}
                            className="bg-emerald-500 hover:bg-emerald-600 text-white"
                            data-testid="ticket-reply-submit"
                          >
                            {replyBusy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            <Send className="w-4 h-4 mr-2" />
                            Send reply
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                ) : null}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
